import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Snowflake } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLines } from "@/components/Reveal";
import CtaBanner from "@/components/CtaBanner";

const MARQUEE_ITEMS = [
  "Residential Air Conditioning",
  "Commercial Air Conditioning",
  "Builder & Project Supply",
  "Refrigerated Cooling",
  "Wholesale Supply Solutions",
  "New Builds & Renovations",
];

const SOLUTIONS = [
  {
    number: "01",
    title: "Residential Air Conditioning",
    text: "Wholesale supply of air conditioning equipment for homes, including solutions suited to new builds, renovations and residential property developments.",
    image: "https://images.unsplash.com/photo-1761330440311-16e160cad236?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    alt: "Modern residential air conditioning in a home interior",
    testId: "service-card-residential",
  },
  {
    number: "02",
    title: "Commercial Air Conditioning",
    text: "Cooling equipment and supply solutions for commercial buildings and business premises, with options suited to different building requirements and project needs.",
    image: "https://images.unsplash.com/photo-1698479603408-1a66a6d9e80f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    alt: "Commercial air conditioning installation on a building",
    testId: "service-card-commercial",
  },
  {
    number: "03",
    title: "Builder & Project Supply",
    text: "Wholesale air conditioning supply for builders, developers and contractors, supporting residential construction, commercial projects and ongoing building requirements.",
    image: "https://images.unsplash.com/photo-1617761141732-d481912af1a9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    alt: "Commercial building project under construction",
    testId: "service-card-builder",
  },
];

const CONSIDERATIONS = [
  { term: "Property Size", detail: "Equipment options are considered against the size of the space to be cooled." },
  { term: "Intended Use", detail: "How a property is used influences the type of system that suits the project." },
  { term: "System Capacity", detail: "Capacity requirements are considered alongside each project's cooling needs." },
  { term: "Installation Requirements", detail: "Installation considerations are factored in when identifying suitable equipment." },
];

const VALUE_PROPS = [
  "Wholesale-focused supply solutions.",
  "Support for residential and commercial projects.",
  "Solutions for builders and property professionals.",
  "A practical approach to project requirements.",
  "A straightforward enquiry process.",
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 64]);

  return (
    <>
      <Seo
        title="Wholesale Air Conditioning & Cooling | GSMG INVESTMENTS"
        description="Wholesale air conditioning and refrigerated cooling solutions for residential, commercial and builder projects. Contact GSMG INVESTMENTS to discuss supply."
      />

      {/* Hero */}
      <section ref={heroRef} className="grain relative overflow-hidden bg-ink text-white">
        <svg className="airflow absolute inset-x-0 top-0 h-full w-full opacity-30" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <path d="M-60 220 C 340 120, 900 320, 1520 180" stroke="#38BDF8" strokeWidth="1.5" fill="none" />
          <path d="M-60 470 C 420 340, 980 560, 1520 420" stroke="#00A8E8" strokeWidth="1.5" fill="none" />
          <path d="M-60 720 C 380 600, 940 790, 1520 660" stroke="#38BDF8" strokeWidth="1.5" fill="none" />
        </svg>

        <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-32 sm:pt-36 lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8 lg:pb-28 lg:pt-44">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-sky-300">Wholesale air conditioning supply</p>
            <h1 data-testid="hero-headline" className="mt-6 font-display text-[2.55rem] font-extrabold leading-[1.04] tracking-tight sm:text-6xl lg:text-[4.4rem]">
              <MaskedLines
                lines={["Wholesale Air Conditioning", "& Refrigerated", "Cooling Solutions"]}
                lineClassName="text-white"
                delay={0.15}
              />
            </h1>
            <Reveal delay={0.55} y={20}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                GSMG INVESTMENTS supplies air conditioning and refrigerated cooling solutions for residential and commercial properties. We work with builders and industry professionals to support projects with suitable cooling equipment and dependable supply solutions.
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Link
                  to="/contact"
                  data-testid="hero-enquire-button"
                  className="inline-flex items-center justify-center gap-2.5 rounded-md bg-brand px-7 py-3.5 font-semibold text-white shadow-lg shadow-brand/25 transition-all hover:bg-sky-400 active:scale-[0.98]"
                >
                  Enquire Now
                  <ArrowRight size={18} aria-hidden="true" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-learn-about-button"
                  className="inline-flex items-center justify-center gap-2.5 rounded-md border border-white/25 px-7 py-3.5 font-semibold text-white transition-all hover:border-sky-400 hover:text-sky-300 active:scale-[0.98]"
                >
                  Learn About Us
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-black/40">
                <img
                  src="https://images.unsplash.com/photo-1667983453881-4992fe86ab1b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400"
                  alt="Commercial air conditioning installation on a modern building"
                  className="h-72 w-full object-cover sm:h-96 lg:h-[27rem]"
                  loading="eager"
                />
                <div className="flex items-center justify-between gap-3 border-t border-white/10 bg-ink px-5 py-3.5">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sky-300">Residential · Commercial · Builders</p>
                  <span className="hidden h-2 w-2 rounded-full bg-brand sm:block" aria-hidden="true" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-t border-white/10 py-6" data-testid="home-marquee">
          <div className="marquee">
            <div className="marquee-track">
              {[0, 1].map((half) => (
                <div key={half} className="flex shrink-0 items-center" aria-hidden={half === 1}>
                  {MARQUEE_ITEMS.map((item) => (
                    <span key={`${half}-${item}`} className="flex items-center gap-8 pr-8">
                      <span className="whitespace-nowrap font-display text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">{item}</span>
                      <Snowflake size={14} className="shrink-0 text-brand" aria-hidden="true" />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Solutions */}
      <section className="bg-mist py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Our solutions</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">Cooling supply for every project type</h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              Air conditioning and refrigerated cooling equipment supplied for homes, commercial buildings and building projects.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <article
                  data-testid={s.testId}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
                >
                  <div className="overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.alt}
                      loading="lazy"
                      className="h-52 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <span className="font-mono text-xs tracking-[0.2em] text-brand">{s.number}</span>
                    <h3 className="mt-3 font-display text-xl font-bold text-ink">{s.title}</h3>
                    <p className="mt-3 flex-1 text-[15px] leading-relaxed text-slate-600">{s.text}</p>
                    <Link
                      to="/contact"
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-brand"
                    >
                      Enquire about this solution
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Refrigerated Cooling Solutions */}
      <section className="grain relative overflow-hidden bg-navy py-20 text-white lg:py-28" data-testid="explainer-section">
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-300">Refrigerated cooling</p>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Refrigerated Cooling Solutions</h2>
              <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                Refrigerated cooling systems use a refrigeration cycle to remove heat from indoor air and provide controlled cooling.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                GSMG INVESTMENTS supplies refrigerated air conditioning solutions for suitable residential and commercial applications, helping customers and builders select equipment appropriate to their project requirements.
              </p>
              <p className="mt-6 text-base leading-relaxed text-slate-300">
                Selecting the right system depends on several project considerations, including:
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-black/30">
                <img
                  src="https://images.unsplash.com/photo-1759262151046-b91fa96e4006?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
                  alt="Modern refrigerated air conditioning unit in a contemporary interior"
                  loading="lazy"
                  className="h-72 w-full object-cover sm:h-96"
                />
              </div>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="explainer-tech-tabs">
            {CONSIDERATIONS.map((c, i) => (
              <Reveal key={c.term} delay={i * 0.1}>
                <div className="h-full rounded-xl border border-white/10 bg-white/5 p-6 transition-colors hover:border-sky-400/40">
                  <span className="font-mono text-xs tracking-[0.2em] text-sky-300">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-white">{c.term}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{c.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Why GSMG INVESTMENTS</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">Why Choose GSMG INVESTMENTS?</h2>
          </Reveal>

          <div className="mt-12">
            {VALUE_PROPS.map((prop, i) => (
              <Reveal key={prop} delay={i * 0.06} y={16}>
                <div
                  data-testid={`value-prop-${i + 1}`}
                  className="group flex items-center gap-6 border-t border-slate-200 py-6 transition-colors last:border-b hover:bg-mist sm:gap-10 sm:px-4"
                >
                  <span className="font-mono text-sm tracking-[0.2em] text-brand">0{i + 1}</span>
                  <p className="flex-1 font-display text-lg font-semibold text-ink sm:text-xl">{prop}</p>
                  <ArrowRight size={20} className="shrink-0 text-slate-300 transition-all group-hover:translate-x-1.5 group-hover:text-brand" aria-hidden="true" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Planning an Air Conditioning Project?"
        text="Get in touch with GSMG INVESTMENTS to discuss your residential, commercial or builder supply requirements."
        testId="home-final-cta-btn"
      />
    </>
  );
}
